import { cloneDeep } from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Badge, Button, Form, FormControl } from 'react-bootstrap';
import { GripVertical } from 'react-bootstrap-icons';
import { API_URL, APP_URL, ENDPOINTS } from '../../helpers/constants';
import { groupEntityTableValues } from '../../helpers/global';
import AppTable from '../common/app-table/AppTable';
import VideoPlayerModal from './VideoPlayerModal';

const reorder = (list, startIndex, endIndex, groupBy, groupValues = []) => {
  const result = cloneDeep(list);

  const itemAboveDestination = result[endIndex - 1];
  const previousItemAtDestination = result[endIndex];

  const [removed] = result.splice(startIndex, 1);

  if (groupBy) {
    const groupOfDestination = previousItemAtDestination.isHeader
      ? endIndex === 0 || endIndex > startIndex
        ? //we are moving below
        previousItemAtDestination.content
        : //we are going above
        itemAboveDestination?.[groupBy]
      : previousItemAtDestination[groupBy];

    if (!groupOfDestination || !groupValues.includes(groupOfDestination)) {
      removed[groupBy] = '';
      result.splice(endIndex, 0, removed);
    } else {
      result.splice(endIndex, 0, { ...removed, [groupBy]: groupOfDestination });
    }
  } else {
    result.splice(endIndex, 0, removed);
  }

  return result.filter(r => !r.isHeader);
};

const getItemStyle = (isDragging, draggableStyle) => ({
  background: isDragging ? '#f7ebd6' : 'white',
  ...draggableStyle
});

const getTableRowKey = (dataObj, rowKeyField, index) =>
  dataObj?.__rowKey ?? dataObj?.[rowKeyField] ?? index;

const getGroupToggleKey = (dataObj, groupBy) => {
  if (dataObj.groupSectionRole) return dataObj.groupSectionRole;
  if (groupBy === 'roles' && Array.isArray(dataObj.roles) && dataObj.roles.length > 0) {
    return dataObj.roles[0];
  }
  return dataObj[groupBy];
};

const getValue = (admin, header, onVideoClick) => {
  if (header.key === 'images') {
    return admin.photos?.length || '-';
  }

  if (header.key === 'virtualTour') {
    const link = admin.virtualTourLink;
    const video = admin.virtualTourVideo;
    if (video && video.trim()) {
      const videoUrl = video.startsWith('http') ? video : `${API_URL || ''}${ENDPOINTS.PUBLIC_VIDEO(video)}`;
      return (
        <Button
          size="sm"
          variant="outline-primary"
          onClick={e => {
            e.stopPropagation();
            if (onVideoClick) onVideoClick(videoUrl);
          }}
        >
          View Video
        </Button>
      );
    }
    // If no video, fall back to link
    if (link && link.trim()) {
      return (
        <Button
          size="sm"
          variant="outline-primary"
          href={link?.startsWith('http') ? link : `https://${link}`}
          target="_blank"
          onClick={e => e.stopPropagation()}
        >
          View Tour
        </Button>
      );
    }
    return '-';
  }

  if (typeof header.renderCell === 'function') {
    return header.renderCell(admin, header);
  }

  let value = header.key === 'salesLocationName' ? admin.salesLocation?.location : admin[header.key];

  if (header.type !== 'boolean' && !value) return '-';
  if (Array.isArray(value) && !value.length) return '-';

  if (Array.isArray(value) && header.key !== 'videoUrls') {
    if (header.key === 'roles') {
      const [first, ...rest] = value;
      const n = rest.length;
      value = n === 0 ? first : `${first} +${n} ${n === 1 ? 'other' : 'others'}`;
    } else {
      value = value.join(', ');
    }
  }

  let returnElement = typeof value === 'object' ? value[header.objectKey] : value;

  if (header.key === 'videoUrls') {
    value = value.map((e, i) => {
      return (
        <a href={e} target="_blank">
          {i !== 0 && ','}Link {i + 1}{' '}
        </a>
      );
    });

    return value;
  }

  switch (header.type) {
    case 'email':
      returnElement = (
        <a onClick={e => e.stopPropagation()} href={`mailto:${value}`}>
          {value}
        </a>
      );

      break;
    case 'phone':
      returnElement = (
        <a onClick={e => e.stopPropagation()} href={`tel:${value}`}>
          {value}
        </a>
      );
      break;
    case 'date':
      returnElement = moment(value).format('MMM DD YYYY');
      break;
    case 'boolean':
      returnElement = returnElement ? 'Yes' : 'No';
      break;
    case 'number': {
      const n = Number(returnElement);
      returnElement = Number.isFinite(n) ? n.toFixed(header.fixedDigits ?? 2) : returnElement ?? '-';
      break;
    }
    case 'range':
      if (value && typeof value === 'object') {
        const hasStart = value.start != null;
        const hasEnd = value.end != null;

        if (hasStart && hasEnd) {
          returnElement = `$${value.start} - $${value.end}`;
        } else if (hasStart && !hasEnd) {
          returnElement = `from $${value.start}`;
        } else if (!hasStart && hasEnd) {
          returnElement = `upto $${value.end}`;
        } else {
          returnElement = '-';
        }
      } else {
        returnElement = '-';
      }
      break;
    case 'appLink':
      returnElement = (
        <a target="_blank" href={`${APP_URL}${value}`}>
          {value?.toString()?.replace('[a-f\\d]{24}', '<id>')}
        </a>
      );
      break;
    case 'buttonLink':
      returnElement = (
        <Button
          size="sm"
          variant="outline-primary"
          href={value?.startsWith('http') ? value : `https://${value}`}
          target="_blank"
          onClick={e => e.stopPropagation()}
        >
          {header.linkName || 'Open'}
        </Button>
      );
      break;
  }

  if (header.showAsBadge) {
    returnElement = (
      <Badge className="p-1 px-2" style={{ fontSize: 12 }} variant={header.badgeVariant || 'primary'}>
        {returnElement}
      </Badge>
    );
  }

  if (header.linkToKey) {
    let url = admin[header.linkToKey] || '';

    if (header.key !== 'videoUrls') {
      if (!url?.startsWith('https://') && !url.startsWith('http://')) {
        url = 'https://' + url;
      }
    }

    returnElement = (
      <a target="_blank" href={url}>
        {returnElement}
      </a>
    );
  }

  return returnElement;
};

const EntityTable = ({
  headers,
  data = [],
  rowKeyField,
  searchQueryKeys = [],
  actionCell,
  additionalComponents,
  additionalHeaderComponentForGroupValues,
  toggleShowMoreSection = null,
  groupBy,
  groupValues,
  draggable,
  onListSort,
  expandedRowKeys = [],
  renderExpandedRow,
  onRowClick,
  quickSearch = true,
  hideUntilSearchKey,
  showActions = true,
  salesLocation,
  showCheckboxes = false,
  selectedRows = [],
  onRowSelect,
  onSelectAll
}) => {
  const [filteredData, setFilteredData] = useState([]);
  const [query, setQuery] = useState('');
  const [videoToPlay, setVideoToPlay] = useState(null);

  const allowDragging = useMemo(() => draggable && !query.trim(), [draggable, query]);

  useEffect(() => {
    if (!salesLocation) {
      const updatedData = data.map(item => {
        const salesLocationName = item?.salesLocation?.location;
        return {
          ...item,
          salesLocationName
        };
      });
      setFilteredData(updatedData);
    } else {
      setFilteredData(data);
    }
  }, [data, salesLocation]);

  useEffect(() => {
    const q = query.trim().toLowerCase();

    if (q.length === 0) {
      setFilteredData(groupEntityTableValues(hideUntilSearchKey ? data.filter(row => !row[hideUntilSearchKey]) : data, groupBy, groupValues));
      return;
    }

    const filteredDatabyQuery =
      data &&
      data.filter(d =>
        searchQueryKeys.some(k => {
          const value = d[k];

          if (k === 'roles' && Array.isArray(d.roles)) {
            return d.roles.some(role => role.toLowerCase().includes(q));
          }

          return value && typeof value === 'string' && value.trim().toLowerCase().includes(q);
        })
      );

    setFilteredData(groupEntityTableValues(filteredDatabyQuery, groupBy, groupValues));
  }, [query, data, groupBy, groupValues, hideUntilSearchKey]);

  const onDragEnd = result => {
    if (!result.destination) {
      return;
    }

    let movedItems = reorder(filteredData, result.source.index, result.destination.index, groupBy, groupValues);
    setFilteredData(groupEntityTableValues(movedItems, groupBy, groupValues));
    onListSort && onListSort(movedItems);
  };

  return (
    <>
      <div className="d-flex mb-1">
        {quickSearch && (
          <div className="flex-grow-1">
            <FormControl
              size={'sm'}
              placeholder="Quick search..."
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
        )}
        {additionalComponents && <div className="ml-2">{additionalComponents()}</div>}
      </div>
      <AppTable>
        <thead>
          <tr className="text-white bg-dark">
            {allowDragging && <th></th>}
            {showCheckboxes && (
              <th style={{ minWidth: 50, width: 50 }}>
                <Form.Check
                  type="checkbox"
                  checked={selectedRows.length === data.length && data.length > 0}
                  onChange={onSelectAll}
                  style={{ cursor: 'pointer' }}
                />
              </th>
            )}
            {headers.map(({ name, minWidth = 100 }) => (
              <th style={{ minWidth }} key={name}>
                {name}
              </th>
            ))}
            {showActions && actionCell && <th style={{ minWidth: 100 }}>Actions</th>}
          </tr>
        </thead>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable isDropDisabled={!allowDragging} droppableId="droppable">
            {provided => (
              <tbody {...provided.droppableProps} ref={provided.innerRef}>
                {filteredData?.length > 0 ? (
                  <>
                    {filteredData.map((dataObj, index) => {
                      const rowKey = getTableRowKey(dataObj, rowKeyField, index);
                      return (
                        <React.Fragment key={rowKey}>
                          {(!groupBy ||
                            dataObj.isHeader ||
                            (toggleShowMoreSection &&
                              toggleShowMoreSection[getGroupToggleKey(dataObj, groupBy) || 'Others'])) && (
                              <Draggable
                                isDragDisabled={dataObj.isHeader || !allowDragging}
                                draggableId={'q-' + rowKey}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <tr
                                    ref={provided.innerRef}
                                    onClick={() => !dataObj.isHeader && onRowClick && onRowClick(dataObj)}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={
                                      dataObj.isHeader
                                        ? 'bg-primary-light text-dark'
                                        : `${onRowClick ? 'hover-light' : ''}`
                                    }
                                    style={{
                                      ...getItemStyle(snapshot.isDragging, provided.draggableProps.style),
                                      zIndex: 0
                                    }}
                                  >
                                    {/* Group Header */}
                                    {dataObj.isHeader ? (
                                      <td colSpan={headers.length + (actionCell ? 1 : 0) + (allowDragging ? 1 : 0) + (showCheckboxes ? 1 : 0)}>
                                        {additionalHeaderComponentForGroupValues &&
                                          additionalHeaderComponentForGroupValues(dataObj.content)}
                                      </td>
                                    ) : (
                                      <>
                                        {allowDragging && (
                                          <td style={{ width: '5%' }}>
                                            <GripVertical />
                                          </td>
                                        )}
                                        {showCheckboxes && (
                                          <td style={{ width: 50 }} onClick={(e) => e.stopPropagation()}>
                                            <Form.Check
                                              type="checkbox"
                                              checked={selectedRows.includes(rowKey)}
                                              onChange={() => onRowSelect(rowKey)}
                                              style={{ cursor: 'pointer' }}
                                            />
                                          </td>
                                        )}
                                        {headers.map(header => (
                                          <td key={header.name}>{getValue(dataObj, header, setVideoToPlay)}</td>
                                        ))}
                                        {showActions && actionCell && <td>{actionCell(dataObj)}</td>}
                                      </>
                                    )}
                                  </tr>
                                )}
                              </Draggable>
                            )}
                          {expandedRowKeys.includes(rowKey) && (
                            <tr>
                              <td
                                colSpan={headers.length + (actionCell ? 1 : 0) + (allowDragging ? 1 : 0) + (showCheckboxes ? 1 : 0)}
                                className="bg-light bg-opacity-50 p-0"
                                style={{ zIndex: 0 }}
                              >
                                <div style={{ width: '90vw' }} className="">
                                  {renderExpandedRow && renderExpandedRow(dataObj)}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </>
                ) : (
                  <tr>
                    <td colSpan={(actionCell ? 1 : 0) + headers.length + (showCheckboxes ? 1 : 0)}>
                      <h6 className="text-muted text-center mb-0">Nothing to show</h6>
                    </td>
                  </tr>
                )}
              </tbody>
            )}
          </Droppable>
        </DragDropContext>
      </AppTable>
      <VideoPlayerModal
        show={!!videoToPlay}
        onClose={() => setVideoToPlay(null)}
        videoUrl={videoToPlay}
        title="Virtual Tour Video"
      />
    </>
  );
};

export default EntityTable;
